import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { FormControl, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { PAYMENT_PROVIDER } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { PaymentIntent, PaymentMethodCreateParams } from '@stripe/stripe-js'
import { distinctUntilChanged, interval, map, Subject, takeUntil } from 'rxjs'

import { TextDialogComponent } from '../../text-dialog/text-dialog.component'
import { PaymentCardFormConfiguration } from '../payment-card-form-configuration'
import { PaymentProviderConfiguration } from '../payment-provider-configuration'
import { StripeFormComponent } from '../stripe-form/stripe-form.component'
import { TilledBillingDetail } from '../tilled'
import { TilledFormComponent } from '../tilled-form/tilled-form.component'

const clientNameStub = /\[CLIENT NAME\]/g

@Component({
  selector: 'mmx-payment-card-form',
  templateUrl: './payment-card-form.component.html',
  styleUrls: ['./payment-card-form.component.scss'],
  standalone: false,
})
export class PaymentCardFormComponent implements OnInit, OnDestroy {
  @Input() providerConfiguration: PaymentProviderConfiguration | undefined
  @Input() formConfiguration: PaymentCardFormConfiguration = {
    forcedSave: false,
    allowSave: false,
  }
  @Input() clientName = ''

  @ViewChild(StripeFormComponent) stripeForm?: StripeFormComponent
  @ViewChild(TilledFormComponent) tilledForm?: TilledFormComponent

  @Output() formUpdated = new EventEmitter<void>()

  PaymentProvider = PAYMENT_PROVIDER
  provider: PAYMENT_PROVIDER = PAYMENT_PROVIDER.STRIPE

  saveControl = new FormControl<boolean>(false)
  saveConsentControl = new FormControl<boolean>(false, [Validators.required])

  private stripeFormCompleted = false
  private onDestroy$ = new Subject<void>()
  private tilledUpdated$ = interval(1000).pipe(
    takeUntil(this.onDestroy$),
    map(() => this.tilledForm?.valid),
    distinctUntilChanged(),
  )

  get valid(): boolean {
    if (!this.stripeFormCompleted && !this.tilledForm?.valid) {
      return false
    }

    if (this.formConfiguration.forcedSave) {
      return this.saveConsentControl.value || false
    }

    return true
  }

  get shouldSaveCard(): boolean {
    return this.formConfiguration.forcedSave || this.saveControl.value || false
  }

  constructor(private dialog: MatDialog, private translateService: TranslateService) {
  }

  ngOnInit(): void {
    if (!this.providerConfiguration) {
      throw new Error('Payment Provider configuration is not provided')
    }

    if (this.providerConfiguration.provider) {
      this.provider = this.providerConfiguration.provider

      if (this.provider === PAYMENT_PROVIDER.TILLED) {
        // since Tilled form doesn't work correctly with the events,
        // we have to manually push the updates using polling
        this.tilledUpdated$.subscribe(() => this.formUpdated.emit())
      }
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  confirmStripePayment(intent: PaymentIntent, billingDetails: PaymentMethodCreateParams.BillingDetails) {
    this.validateStripe()

    if (!intent.client_secret) {
      throw new Error('Stripe Payment Intent client secret is missing')
    }

    // ts ignore is added for preventing error of the nullable stripeForm
    // covered in the validateStripe method
    // @ts-ignore
    return this.stripeForm.confirmPayment(intent.client_secret, billingDetails)
  }

  confirmSetupIntent(clientSecret: string, billingDetails: PaymentMethodCreateParams.BillingDetails) {
    this.validateStripe()

    // ts ignore is added for preventing error of the nullable stripeForm
    // covered in the validateStripe method
    // @ts-ignore
    return this.stripeForm.confirmSetupIntent(clientSecret, billingDetails)
  }

  createTilledPaymentMethod(billingDetail: TilledBillingDetail) {
    if (this.provider !== PAYMENT_PROVIDER.TILLED) {
      throw new Error('Payment card form provider is not Tilled')
    }

    if (!this.tilledForm) {
      throw new Error('Tilled form does not exist')
    }

    return this.tilledForm.createPaymentMethod(billingDetail)
  }

  openTerms(event: MouseEvent) {
    event.preventDefault()
    const text: string = this.translateService.instant('payment-card.terms-text')
    this.dialog.open(TextDialogComponent, { data: text.replace(clientNameStub, this.clientName) })
  }

  stripeUpdated(completed: boolean) {
    this.stripeFormCompleted = completed
    this.formUpdated.emit()
  }

  private validateStripe() {
    if (this.provider !== PAYMENT_PROVIDER.STRIPE) {
      throw new Error('Payment card form provider is not Stripe')
    }

    if (!this.stripeForm) {
      throw new Error('Stripe form does not exist')
    }
  }
}
